import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { XMarkIcon, ClockIcon } from '@heroicons/react/24/outline'

interface RescheduleModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (newDateTime: string) => void
  title: string
  currentDateTime: string
}

const RescheduleModal: React.FC<RescheduleModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  currentDateTime,
}) => {
  const [date, setDate] = useState('')
  const [time, setTime] = useState('')
  const [error, setError] = useState('')

  // Initialize with current scheduled time
  useEffect(() => {
    if (isOpen && currentDateTime) {
      // CRITICAL: Parse as UTC by appending 'Z' if not already present
      // Backend returns MySQL datetime format without timezone indicator
      const timeStr = currentDateTime.includes('Z') ? currentDateTime : currentDateTime + 'Z'
      const dt = new Date(timeStr)

      // Format for datetime-local input: YYYY-MM-DDTHH:MM
      // Use local time for input fields (browser will handle timezone conversion)
      const dateStr = dt.toISOString().slice(0, 10)
      const hours = dt.getHours().toString().padStart(2, '0')
      const minutes = dt.getMinutes().toString().padStart(2, '0')
      const timeInputStr = `${hours}:${minutes}`

      setDate(dateStr)
      setTime(timeInputStr)
      setError('')
    }
  }, [isOpen, currentDateTime])

  const handleConfirm = () => {
    // Validate inputs
    if (!date || !time) {
      setError('Please enter both date and time')
      return
    }

    // Combine date and time
    const newDateTime = `${date} ${time}`
    const newDate = new Date(newDateTime)

    if (isNaN(newDate.getTime())) {
      setError('Invalid date or time format')
      return
    }

    // Check if date is in the past
    if (newDate < new Date()) {
      setError('Please select a future date and time')
      return
    }

    onConfirm(newDateTime)
    onClose()
  }

  const formatCurrentTime = (dateTime: string) => {
    // CRITICAL: Parse as UTC by appending 'Z' if not already present
    const timeStr = dateTime.includes('Z') ? dateTime : dateTime + 'Z'
    const dt = new Date(timeStr)
    return dt.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg"
              >
                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-purple-100 sm:mx-0 sm:h-10 sm:w-10">
                      <ClockIcon className="h-6 w-6 text-purple-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold leading-6 text-gray-900">
                          Reschedule Post
                        </h3>
                        <button
                          onClick={onClose}
                          className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <XMarkIcon className="h-6 w-6" />
                        </button>
                      </div>
                      <div className="mt-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">
                          {title}
                        </p>
                        <div className="bg-gray-50 rounded-md p-3 mb-4">
                          <p className="text-xs text-gray-500">Current schedule:</p>
                          <p className="text-sm font-medium text-gray-900">
                            {formatCurrentTime(currentDateTime)}
                          </p>
                        </div>

                        {/* Date Input */}
                        <div className="mb-4">
                          <label htmlFor="reschedule-date" className="block text-sm font-medium text-gray-700 mb-1">
                            New Date
                          </label>
                          <input
                            type="date"
                            id="reschedule-date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm px-3 py-2 border"
                          />
                        </div>

                        {/* Time Input */}
                        <div className="mb-4">
                          <label htmlFor="reschedule-time" className="block text-sm font-medium text-gray-700 mb-1">
                            New Time
                          </label>
                          <input
                            type="time"
                            id="reschedule-time"
                            value={time}
                            onChange={(e) => setTime(e.target.value)}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm px-3 py-2 border"
                          />
                        </div>

                        {/* Error Message */}
                        {error && (
                          <div className="rounded-md bg-red-50 p-3 mb-4">
                            <p className="text-sm text-red-800">{error}</p>
                          </div>
                        )}

                        {/* Preview */}
                        {date && time && !error && (
                          <div className="bg-purple-50 rounded-md p-3">
                            <p className="text-xs text-purple-700 mb-1">New schedule will be:</p>
                            <p className="text-sm font-medium text-purple-900">
                              {new Date(`${date} ${time}`).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                              })}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-3">
                  <button
                    type="button"
                    onClick={handleConfirm}
                    className="inline-flex w-full justify-center rounded-md bg-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-700 sm:w-auto focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    Reschedule
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}

export default RescheduleModal
